import { useState, useMemo, useCallback } from 'react'
import TrackerCaseDrawer from './TrackerDetailDrawer'
import { LANES, LANE_BY_ID, prettyStatus, fmtDate } from './trackerFields'
import { Spinner } from './trackerShared'

// The standup board: one column per pipeline lane, cases stacked inside.
// HORIZONTAL across the pipeline, VERTICAL within a lane — a single full-width
// list could not carry ~50 cases, and the shape of the pipeline is itself the
// information ("we are 26-deep in Blocked").
//
// The lane a case sits in comes from col F of the sheet (backend `stageOf`);
// this file only draws it. NO PATIENT DATA — a case is its Case ID.
//
// Every lane sub-groups, and both axes collapse:
//   - lanes collapse sideways to a rail (a count)
//   - groups collapse downwards to a header (a count)
// Expand all lanes + collapse all groups = the whole pipeline as a count matrix
// on one screen, which is the fastest read of "where is everything".

const LANE_W = 'w-[286px]'
const RAIL_W = 'w-[104px]'
// Lanes scroll inside themselves; the page must never scroll horizontally.
// max-h, NOT a fixed h: a lane sizes to its content and only starts scrolling
// once it outgrows the viewport. A fixed height made a 1-card lane as tall as a
// 26-card one, and made the all-groups-collapsed summary a row of mostly-empty
// columns — which is precisely the view that is supposed to be compact.
// Ragged column heights are normal for a board and are the honest rendering.
const LANE_H = 'max-h-[calc(100vh-21rem)]'

const LS = {
  groupBy: 'tracker.board.groupBy',
  lanes: 'tracker.board.collapsedLanes',
  groups: 'tracker.board.collapsedGroups',
}
const loadSet = (key) => {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')) } catch { return new Set() }
}
const saveSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])) } catch { /* private mode */ }
}

const GROUP_BY = [
  { id: 'status', label: 'Status' },
  { id: 'owner', label: 'Owner' },
]
// Collapsed-group keys carry the DIMENSION. Without it, "Collapse all" while
// grouped by Owner would replace the whole set and silently discard whatever
// was collapsed under Status.
const groupKeyOf = (dim, laneId, name) => `${dim}::${laneId}::${name}`

// Age is the urgency signal here: Dispatch-Plan is filled on 4 of ~50 cases, so
// a due-date-driven board would be blank. The ramp is deliberately gentle —
// nothing here is "bad" for being 30 days old when it is waiting on a customer.
function ageTone(days, suspect) {
  if (suspect || days == null) return 'bg-gray-100 text-gray-500'
  if (days >= 90) return 'bg-red-100 text-red-700'
  if (days >= 30) return 'bg-amber-100 text-amber-700'
  return 'bg-gray-100 text-gray-600'
}

function CaseCard({ c, onOpen, showStatus, showOwner }) {
  const lane = LANE_BY_ID[c.lane]
  const due = c.daysToDispatch
  const dueLabel = c.dispatchPlan == null ? null
    : due == null ? fmtDate(c.dispatchPlan)
    : due < 0 ? `${-due}d overdue`
    : due === 0 ? 'due today'
    : `due in ${due}d`
  const dueTone = due != null && due <= 0 ? 'text-red-700 font-semibold'
    : due != null && due <= 3 ? 'text-amber-700'
    : 'text-gray-500'

  return (
    <button
      type="button"
      onClick={() => onOpen(c.id)}
      className={`w-full text-left bg-white border border-gray-200 rounded-lg p-2.5 hover:border-gray-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 transition ${c.overdue ? 'border-l-2 border-l-red-500' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-[11px] font-semibold text-gray-800 leading-tight break-all">
          {c.caseNo}
          {/* A case whose STLs disagree must stay distinguishable even when the
              group header states the status and the chip below is hidden —
              otherwise the default view cannot tell a mixed case from a uniform one. */}
          {c.stageMixed && !showStatus && (
            <span
              className="ml-1 text-amber-600 font-sans"
              title={`STLs disagree: ${Object.entries(c.openStates || {}).map(([k, n]) => `${n}× ${k}`).join(', ')}`}
            >+</span>
          )}
          {/* An STL carrying a col-F word the board cannot place. This used to
              announce itself by dragging the whole case into Unmapped, which was
              loud but wrong — the case left its real lane. Now the case files
              under its earliest RECOGNISED stage and the drift shows here, so it
              is still visible without distorting the board. Shown in every
              grouping, unlike `+`, because the group header never states it. */}
          {c.unknownStates?.length > 0 && (
            <span
              className="ml-1 text-rose-500 font-sans"
              title={`Status the board does not recognise: ${c.unknownStates.join(', ')} — add it to a lane, or fix the sheet`}
            >?</span>
          )}
        </span>
        <span
          className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded tabular-nums ${ageTone(c.ageDays, c.ageSuspect)}`}
          title={c.ageSuspect
            ? `DICOM date reads ${c.ageDays}d ago — check the date cell in the sheet`
            : c.dicomReceivedDate ? `DICOM received ${fmtDate(c.dicomReceivedDate)}` : 'No DICOM date in the sheet'}
        >
          {c.ageSuspect ? '⚑ date?' : c.ageDays == null ? '—' : `${c.ageDays}d`}
        </span>
      </div>

      {/* Whatever the group header already states — status, or owner — is not
          repeated on the card. Each field shows only when it is NOT the thing
          the cards are grouped by. */}
      {showStatus && c.stage && (
        <div className="mt-1.5">
          <span
            className={`inline-block max-w-full truncate text-[11px] px-1.5 py-0.5 rounded ${lane?.head || 'text-gray-600'} bg-gray-50 border border-gray-200`}
            title={c.stageMixed ? `${c.stage} — STLs disagree: ${Object.entries(c.openStates || {}).map(([k, n]) => `${n}× ${k}`).join(', ')}` : c.stage}
          >
            {prettyStatus(c.stage)}{c.stageMixed ? ' +' : ''}
          </span>
        </div>
      )}

      <div className="mt-1.5 text-[11px] text-gray-600 truncate" title={[c.doctorName, c.hospitalName].filter(Boolean).join(' · ')}>
        {c.doctorName || <span className="italic text-gray-400">no doctor</span>}
        {c.hospitalName && <span className="text-gray-400"> · {c.hospitalName}</span>}
      </div>

      <div className="mt-1 flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[10px] text-gray-500">
        {showOwner && <>
          <span className="truncate max-w-[110px]">{c.owner || <span className="italic text-gray-400">unassigned</span>}</span>
          <span className="text-gray-300">·</span>
        </>}
        {/* "10/12" reads as 10 DONE; it is 10 LEFT. Spell it out. */}
        <span className="tabular-nums">
          {c.pending === 0 ? `${c.partCount} STL${c.partCount === 1 ? '' : 's'} done` : `${c.pending} of ${c.partCount} left`}
        </span>
        {dueLabel && <><span className="text-gray-300">·</span><span className={dueTone}>{dueLabel}</span></>}
      </div>
    </button>
  )
}

// Split a lane's cases into sub-groups. One code path for both dimensions:
// grouping by status keys on `stage`, which for the Blocked lane is already the
// verbatim blocking reason (stageOf guarantees reasons[0] === label), so this
// reproduces the old Blocked-only behaviour rather than special-casing it.
// Count descending, then alphabetical, so the mass of the work is at the top.
export function groupCases(cases, dimension) {
  const keyOf = dimension === 'owner'
    ? (c) => c.owner || 'Unassigned'
    : (c) => c.stage || 'No status'
  const groups = new Map()
  for (const c of cases) {
    const k = keyOf(c)
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k).push(c)
  }
  return [...groups.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
}

function LaneGroup({ groupKey, name, rows, collapsed, onToggle, onOpen, showStatus, showOwner, muted }) {
  return (
    <div className="space-y-2">
      <div className="sticky -top-2 z-[5] -mx-2.5 px-2.5 py-1 bg-gray-50/95 backdrop-blur">
        <button
          type="button"
          onClick={() => onToggle(groupKey)}
          aria-expanded={!collapsed}
          className="w-full flex items-center gap-1.5 text-left group"
        >
          <span className="text-gray-400 text-[10px] w-2 shrink-0">{collapsed ? '›' : '⌄'}</span>
          <span className={`text-[10px] uppercase tracking-wide font-semibold truncate ${muted ? 'text-gray-400' : 'text-gray-600'} group-hover:text-gray-800`}>
            {prettyStatus(name)}
          </span>
          <span className="text-[10px] text-gray-400 tabular-nums ml-auto shrink-0">{rows.length}</span>
        </button>
      </div>
      {!collapsed && rows.map((c) => <CaseCard key={c.id} c={c} onOpen={onOpen} showStatus={showStatus} showOwner={showOwner} />)}
    </div>
  )
}

function Lane({ lane, cases, collapsed, onToggle, onOpen, groupBy, collapsedGroups, onToggleGroup }) {
  const isRail = collapsed || cases.length === 0
  const canExpand = cases.length > 0

  if (isRail) {
    // No LANE_H here — a collapsed rail sizes to its content. The row is
    // items-start, so short rails sit beside tall lanes without stretching.
    return (
      <div className={`${RAIL_W} shrink-0 rounded-xl border border-gray-200 bg-gray-50/60`}>
        <button
          type="button"
          onClick={canExpand ? onToggle : undefined}
          disabled={!canExpand}
          aria-expanded={false}
          className={`w-full flex flex-col items-center gap-2 py-3 rounded-xl ${canExpand ? 'hover:bg-gray-100/70 cursor-pointer' : 'cursor-default'}`}
          title={canExpand ? `Show ${cases.length} ${lane.label} case${cases.length === 1 ? '' : 's'}` : lane.empty}
        >
          <span className={`h-1.5 w-8 rounded-full ${cases.length ? lane.accent : 'bg-gray-200'}`} />
          <span className="text-xl font-semibold text-gray-700 tabular-nums leading-none">{cases.length || '—'}</span>
          <span className="text-[10px] text-gray-500 leading-tight px-1.5 text-center">{lane.label}</span>
          {canExpand && <span className="text-gray-400 text-xs">›</span>}
        </button>
      </div>
    )
  }

  const groups = groupCases(cases, groupBy)
  // A lane whose cases all share one status gets a header restating the lane.
  // Kept for consistency, toned down so it does not read as content.
  const singleton = groupBy === 'status' && groups.length === 1

  return (
    <div className={`${LANE_W} ${LANE_H} shrink-0 flex flex-col rounded-xl border border-gray-200 bg-gray-50/60`}>
      <div className="sticky top-0 z-10 px-2.5 pt-2.5 pb-2 bg-gray-50/95 backdrop-blur rounded-t-xl border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className={`h-1.5 w-6 rounded-full ${lane.accent}`} />
          <span className="text-xs font-semibold text-gray-700 flex-1 truncate">{lane.label}</span>
          <span className="text-xs text-gray-500 tabular-nums">{cases.length}</span>
          <button
            type="button" onClick={onToggle} aria-expanded
            className="text-gray-400 hover:text-gray-600 text-xs leading-none px-1"
            title={`Collapse ${lane.label}`} aria-label={`Collapse ${lane.label}`}
          >‹</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2.5 py-2 space-y-2">
        {groups.map(([name, rows]) => (
          <LaneGroup
            key={name} groupKey={groupKeyOf(groupBy, lane.id, name)} name={name} rows={rows}
            collapsed={collapsedGroups.has(groupKeyOf(groupBy, lane.id, name))}
            onToggle={onToggleGroup} onOpen={onOpen}
            showStatus={groupBy !== 'status'} showOwner={groupBy !== 'owner'} muted={singleton}
          />
        ))}
      </div>
    </div>
  )
}

function Segmented({ label, children }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-gray-500">{label}</span>
      <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden bg-white">{children}</div>
    </div>
  )
}
const SegBtn = ({ onClick, children, active }) => (
  <button
    type="button" onClick={onClick}
    className={`text-[11px] px-2.5 py-1.5 ${active ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
  >{children}</button>
)

export default function TrackerBoard({ board = [], summary, loading, error }) {
  const [selected, setSelected] = useState(null)
  const [q, setQ] = useState('')
  const [groupBy, setGroupBy] = useState(() => {
    const v = localStorage.getItem(LS.groupBy)
    return GROUP_BY.some((g) => g.id === v) ? v : 'status'
  })
  // Untriaged starts collapsed: a data-quality nudge, not today's work.
  const [collapsedLanes, setCollapsedLanes] = useState(() => {
    const stored = localStorage.getItem(LS.lanes)
    return stored ? loadSet(LS.lanes) : new Set(['untriaged'])
  })
  const [collapsedGroups, setCollapsedGroups] = useState(() => loadSet(LS.groups))

  const setGroupByPref = (v) => { setGroupBy(v); localStorage.setItem(LS.groupBy, v) }
  const updLanes = (next) => { setCollapsedLanes(next); saveSet(LS.lanes, next) }
  const updGroups = (next) => { setCollapsedGroups(next); saveSet(LS.groups, next) }

  const lanes = useMemo(() => {
    const defs = summary?.lanes?.length
      ? summary.lanes.map((l) => ({ ...LANE_BY_ID[l.id], ...l })) // backend order wins
      : LANES
    const s = q.trim().toLowerCase()
    // ...c.unknownStates: `stage` is now the RECOGNISED label, so without this
    // the offending sheet word is unsearchable — and with the Unmapped lane no
    // longer collecting these cases, search is the only way to reach them from
    // the "Unknown status N" stat.
    const rows = !s ? board : board.filter((c) =>
      [c.caseNo, c.doctorName, c.hospitalName, c.owner, c.stage, ...(c.unknownStates || [])]
        .some((v) => (v || '').toLowerCase().includes(s)))
    return defs.map((l) => ({ lane: l, cases: rows.filter((c) => c.lane === l.id) }))
  }, [board, summary, q])

  const shown = lanes.reduce((n, l) => n + l.cases.length, 0)

  const toggleLane = (id) => {
    const n = new Set(collapsedLanes)
    n.has(id) ? n.delete(id) : n.add(id)
    updLanes(n)
  }
  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const n = new Set(prev)
      n.has(key) ? n.delete(key) : n.add(key)
      saveSet(LS.groups, n)
      return n
    })
  }, [])

  // "Collapse all groups" has to enumerate the groups that currently exist,
  // which depends on the filter and the grouping dimension — so it is computed
  // from what is on screen, not from a static list.
  const allGroupKeys = useMemo(
    () => lanes.flatMap(({ lane, cases }) => groupCases(cases, groupBy).map(([name]) => groupKeyOf(groupBy, lane.id, name))),
    [lanes, groupBy],
  )

  // everything collapsed under the OTHER dimension, preserved across a
  // collapse-all / expand-all on this one
  const otherDimGroups = [...collapsedGroups].filter((k) => !k.startsWith(`${groupBy}::`))

  if (loading) return <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center"><Spinner className="h-4 w-4" /> Loading the board…</div>
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>

  return (
    // overflow-x-clip contains the full-bleed strip below. `50vw` includes the
    // vertical scrollbar's width, so on classic (non-overlay) scrollbars the
    // strip would overhang the body and give the PAGE a horizontal scrollbar.
    // `clip` rather than `hidden`: hidden would make this a scroll container
    // and break the lanes' sticky headers.
    <div className="overflow-x-clip">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search case, doctor, hospital, owner, status…"
          className="input flex-1 min-w-[220px] max-w-sm text-sm"
        />
        {q && <span className="text-xs text-gray-500 tabular-nums">{shown} of {board.length}</span>}

        <Segmented label="Group by">
          {GROUP_BY.map((g) => (
            <SegBtn key={g.id} active={groupBy === g.id} onClick={() => setGroupByPref(g.id)}>{g.label}</SegBtn>
          ))}
        </Segmented>

        <Segmented label="Lanes">
          <SegBtn onClick={() => updLanes(new Set(lanes.map(({ lane }) => lane.id)))}>Collapse all</SegBtn>
          <SegBtn onClick={() => updLanes(new Set())}>Expand all</SegBtn>
        </Segmented>

        <Segmented label="Groups">
          <SegBtn onClick={() => updGroups(new Set([...otherDimGroups, ...allGroupKeys]))}>Collapse all</SegBtn>
          <SegBtn onClick={() => updGroups(new Set(otherDimGroups))}>Expand all</SegBtn>
        </Segmented>
      </div>

      {/* The lane strip is FULL-BLEED: the portal shell is max-w-7xl, which fits
          only four lanes, and the shape of the pipeline is the information here.
          Verified at 1280/1600/2100px not to give the PAGE a horizontal
          scrollbar — only the strip scrolls. */}
      {board.length === 0 ? (
        <div className="text-center text-gray-500 text-sm py-12 border border-dashed border-gray-200 rounded-xl">
          Nothing open. Every 2026 case has a terminal Status in col F of the sheet.
        </div>
      ) : (
        <div className="overflow-x-auto pb-2 mx-[calc(50%-50vw)] px-[calc(50vw-50%)]">
          <div className="flex gap-3 items-start min-w-min">
            {lanes.map(({ lane, cases }) => (
              <Lane
                key={lane.id} lane={lane} cases={cases}
                collapsed={collapsedLanes.has(lane.id)}
                onToggle={() => toggleLane(lane.id)}
                onOpen={setSelected}
                groupBy={groupBy}
                collapsedGroups={collapsedGroups}
                onToggleGroup={toggleGroup}
              />
            ))}
          </div>
        </div>
      )}

      {selected && <TrackerCaseDrawer caseId={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
